import React, { useEffect, useMemo, useRef } from 'react';

import { theme } from 'antd';

import type { ChartOptions } from 'chart.js';
import Chart from 'chart.js';
import { STORAGE_KEY, readCookie } from 'v2/components/Shared/ValuesVisibility';

interface IProps {
  [key: string]: any;
  id: string;
  options: ChartOptions;
  tooltipVariant?: 'default' | 'light';
}

function createCustomTooltip(backgroundColor: string, textColor: string, borderColor: string, boxShadow: string) {
  return function customTooltip(this: any, tooltipModel: any) {
    let tooltipEl = document.getElementById('chartjs-tooltip');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<table></table>';
      document.body.appendChild(tooltipEl);
    }

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    tooltipEl.classList.remove('above', 'below', 'no-transform');

    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    function getBody(bodyItem: any) {
      return bodyItem.lines;
    }

    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || [];
      const bodyLines = tooltipModel.body.map(getBody);

      let innerHtml: any = '<thead>';

      titleLines.forEach(function (title: any) {
        innerHtml += '<tr><th>' + title + '</th></tr>';
      });
      innerHtml += '</thead><tbody>';

      bodyLines.forEach(function (body: any, i: any) {
        const shouldHideValue = readCookie(STORAGE_KEY) === 'hidden';
        const label = body[0].split(': ')[0];
        const value = shouldHideValue ? '••••' : body[0].split(': ')[1];
        const colors = tooltipModel.labelColors[i];
        let style = 'background:' + colors.backgroundColor;
        style += '; border-color:' + colors.borderColor;
        style +=
          '; border-width: 2px; display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px';
        const span = '<span style="' + style + '"></span>';
        innerHtml += `<tr><td>${span + label}<strong style="margin-left: 16px">${value}</strong></td></tr>`;
      });
      innerHtml += '</tbody>';

      const tableRoot: any = tooltipEl.querySelector('table');
      tableRoot.innerHTML = innerHtml;
    }

    // `this` is the Chart.js tooltip context
    const position = this._chart.canvas ? this._chart.canvas.getBoundingClientRect() : 0;

    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
    tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
    tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
    tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
    tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.backgroundColor = backgroundColor;
    tooltipEl.style.color = textColor;
    tooltipEl.style.zIndex = '999';
    tooltipEl.style.border = `1px solid ${borderColor}`;
    tooltipEl.style.borderRadius = '12px';
    tooltipEl.style.boxShadow = boxShadow;
  };
}

const CustomChart: React.FC<IProps> = ({ id, options, tooltipVariant = 'default', ...rest }) => {
  const { token } = theme.useToken();
  const chartRef = useRef<any>(null);
  const isLightTooltip = tooltipVariant === 'light';
  const customTooltip = useMemo(
    () =>
      createCustomTooltip(
        isLightTooltip ? token.colorBgElevated : token.colorBgSpotlight,
        isLightTooltip ? token.colorText : token.colorTextLightSolid,
        isLightTooltip ? token.colorBorderSecondary : 'transparent',
        isLightTooltip ? token.boxShadowSecondary : 'none'
      ),
    [
      isLightTooltip,
      token.boxShadowSecondary,
      token.colorBgElevated,
      token.colorBgSpotlight,
      token.colorBorderSecondary,
      token.colorText,
      token.colorTextLightSolid
    ]
  );

  useEffect(() => {
    chartRef.current = new Chart(id, {
      type: rest.type,
      data: {
        labels: rest.data.labels,
        datasets: rest.data.datasets
      },
      options: {
        ...options,
        tooltips: {
          ...options.tooltips,
          enabled: false,
          custom: customTooltip
        }
      }
    });

    chartRef.current.generateLegend();

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, customTooltip]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    chartRef.current.data.datasets = rest.data.datasets;
    chartRef.current.data.labels = rest.data.labels;
    chartRef.current.update();
  }, [rest.data.datasets, rest.data.labels]);

  return <canvas width='0' height='0' id={id} />;
};

export default CustomChart;